import { useLocation, Link } from "react-router-dom";
import { Breadcrumbs as MUIBreadcrumbs, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import AutoGraphIcon from "@mui/icons-material/AutoGraph";

// ⬇️ Import your SVG icons
import { ReactComponent as DashboardIcon } from "../assets/icons/cube.svg";
import { ReactComponent as DatabaseIcon } from "../assets/icons/database.svg";
import { ReactComponent as NewRunIcon } from "../assets/icons/setting.svg";
import { ReactComponent as DictionariesIcon } from "../assets/icons/book.svg";
import { ReactComponent as BillingIcon } from "../assets/icons/credit-card.svg";
import { ReactComponent as SettingsIcon } from "../assets/icons/setting.svg";

// Depth-1 routes don't need breadcrumbs — the active sidebar item already
// shows "you are here," and the page's own h5/h6 is the title. Duplicate
// labels are visual noise. Export so Main.jsx can hide the sticky strip
// entirely on these pages.
const DEPTH_ONE_PATHS = new Set([
  "/", "/data", "/analyze", "/settings", "/billing", "/dictionaries",
]);
export function shouldShowBreadcrumbs(pathname) {
  const p = (pathname || "").replace(/\/+$/, "") || "/";
  return !DEPTH_ONE_PATHS.has(p);
}

export default function Breadcrumbs() {
  const location = useLocation();
  const theme = useTheme();

  if (!shouldShowBreadcrumbs(location.pathname)) return null;

  const pathnames = location.pathname.split("/").filter((x) => x);

  const fontSize = theme.typography.breadcrumb?.fontSize || theme.typography.paragraph.fontSize;
  const fontFamily = theme.typography.breadcrumb?.fontFamily || theme.typography.fontFamily;
  const iconSize = `calc(${fontSize} + 2px)`;

  const labelMap = {
      analyze: {
        label: "Explore",
        icon: <AutoGraphIcon style={{ width: iconSize, height: iconSize }} />,
      },
      data: {
        label: "Data",
        icon: <DatabaseIcon style={{ width: iconSize, height: iconSize }} />,
      },
      runs: {
        label: "Analyses",
        icon: <NewRunIcon style={{ width: iconSize, height: iconSize }} />,
      },
      new: {
        label: "New",
        icon: <NewRunIcon style={{ width: iconSize, height: iconSize }} />,
      },
      dictionaries: {
        label: "Dictionaries",
        icon: <DictionariesIcon style={{ width: iconSize, height: iconSize }} />,
      },
      billing: {
        label: "Billing",
        icon: <BillingIcon style={{ width: iconSize, height: iconSize }} />,
      },
      settings: {
        label: "Settings",
        icon: <SettingsIcon style={{ width: iconSize, height: iconSize }} />,
      },
    };

  return (
    <MUIBreadcrumbs
      aria-label="breadcrumb"
      sx={{
        fontSize,
        fontFamily,
      }}
    >
      {(() => {
        const crumbs = [];
        const pathname = location.pathname;

        // Helper to push a crumb
        const pushCrumb = (label, icon, to, isLast = false) => {
          const content = (
            <>
              {icon}
              {label}
            </>
          );
          if (isLast || !to) {
            crumbs.push(
              <Typography key={`${label}-${to || 'last'}`} color="text.primary" sx={{ fontSize, fontFamily, display: 'flex', alignItems: 'center', gap: '6px' }}>
                {content}
              </Typography>
            );
          } else {
            crumbs.push(
              <Link key={to} to={to} style={{ textDecoration: 'none', color: 'inherit', fontSize, fontFamily, display: 'flex', alignItems: 'center', gap: '6px' }}>
                {content}
              </Link>
            );
          }
        };

        // Home (/)
        if (pathname === "/") {
          pushCrumb("Projects", <DashboardIcon style={{ width: iconSize, height: iconSize }} />, null, true);
          return crumbs;
        }

        // Project-scoped analyze session (/projects/:id/analyze/:sessionId[/report])
        // The project page's header strip is the project's title — no need to
        // repeat the project id (UUID, meaningless to users) in the crumb.
        // Just show: Projects > Insights (> Report).
        if (pathname.startsWith("/projects/") && pathnames.length >= 4 && pathnames[2] === "analyze") {
          pushCrumb("Projects", <DashboardIcon style={{ width: iconSize, height: iconSize }} />, "/");
          const isReport = pathnames[4] === "report";
          if (isReport) {
            pushCrumb("Insights", null, pathname.replace("/report", ""));
            pushCrumb("Report", null, null, true);
          } else {
            pushCrumb("Insights", null, null, true);
          }
          return crumbs;
        }

        // Project detail (/projects/:id) — just a "back to Projects" crumb.
        // The ProjectHeaderStrip below renders the project name as h6, so we
        // skip the UUID crumb that would just say "4e85ae93-c014-…" and add
        // nothing for the reader.
        if (pathname.startsWith("/projects/")) {
          pushCrumb("Projects", <DashboardIcon style={{ width: iconSize, height: iconSize }} />, null, true);
          return crumbs;
        }

        // Runs detail (/runs/:id...)
        if (pathname.startsWith("/runs")) {
          // root
          pushCrumb("Projects", <DashboardIcon style={{ width: iconSize, height: iconSize }} />, "/");
          
          // Special handling for /runs/:id/notebook
          if (pathname.endsWith("/notebook") && pathnames.length >= 3) {
            const id = pathnames[1] ? decodeURIComponent(pathnames[1]) : "";
            if (id) pushCrumb(id, null, `/runs/${encodeURIComponent(id)}`);
            pushCrumb("Decisions", null, null, true);
            return crumbs;
          }

          // Special handling for /runs/:id/data
          if (pathname.endsWith("/data") && pathnames.length >= 3) {
            const id = pathnames[1] ? decodeURIComponent(pathnames[1]) : "";
            if (id) pushCrumb(id, null, `/runs/${encodeURIComponent(id)}`);
            pushCrumb("Detailed data", null, null, true);
            return crumbs;
          }
          
          // other segments (hide the literal 'runs')
          let builtPath = "";
          pathnames.forEach((seg, idx) => {
            builtPath += `/${seg}`;
            if (idx === 0 && seg === 'runs') return; // skip the segment label
            const mapped = labelMap[seg] || { label: decodeURIComponent(seg), icon: null };
            const isLast = idx === pathnames.length - 1;
            pushCrumb(mapped.label, mapped.icon, isLast ? null : builtPath, isLast);
          });
          return crumbs;
        }

        // Default behavior for other pages: do NOT prepend Projects
        let builtPath = "";
        pathnames.forEach((seg, idx) => {
          builtPath += `/${seg}`;
          const mapped = labelMap[seg] || { label: decodeURIComponent(seg), icon: null };
          const isLast = idx === pathnames.length - 1;
          pushCrumb(mapped.label, mapped.icon, isLast ? null : builtPath, isLast);
        });
        return crumbs;
      })()}
    </MUIBreadcrumbs>
  );
}